import { ComponentType } from 'react';

import { COUNTRY } from 'common/constants';
import { FormValidator } from 'common/Validator/Validator';
import { CheckoutState } from 'pages/checkout/connect/types';

import { INSURANCE_FORM_VALIDATOR_CONFIG, InsuranceInfo } from 'pages/checkout/steps/Insurance/InsuranceInfo';

import { PATIENT_INFO_VALIDATOR_CONFIG, PatientInfo } from 'pages/checkout/steps/Insurance/PatientInfo';

import { OngoingCareQuestion } from 'pages/checkout/steps/OngoingCareQuestion/OngoingCareQuestion';
import { PhoneNumber } from 'pages/checkout/steps/PhoneNumber/PhoneNumber';

import { Clinic } from 'types/Clinic';

import { Confirm } from './Confirm/Confirm';
import { Info } from './Info/Info';
import { TreatmentsQuestion } from './TreatmentsQuestion/TreatmentsQuestion';
import { Verify } from './Verify/Verify';

export enum StepId {
  INFO = 'info',
  PHONE_NUMBER = 'phone-number',
  ONGOING_CARE = 'ongoing-care',
  TREATMENTS = 'treatments',
  PATIENT_INFO = 'patient-info',
  INSURANCE = 'insurance',
  VERIFY = 'verify',
  CONFIRM = 'confirm',
}

export interface CheckoutStepParams {
  checkoutState: CheckoutState;
  isUserAuthenticated: boolean;
  patient: any;
  match: any;
  clinic: Clinic;
  checkoutOngoingCareAnswer: string | null;
  checkoutSelectedTreatments: string[] | null;
}

export interface CheckoutStepDefinition {
  /**
   * The unique identifier for the step
   */
  id: StepId;

  /**
   * Whether to show the step in the progress bar
   */
  showInProgress: boolean;

  /**
   * The display name for the step in the progress bar
   */
  name: string;

  /**
   * The URL path for the step (E.g. /checkout/:path)
   */
  path: string;

  /**
   * The Redux key for the step completion status (E.g. checkoutData.stepCompletion[reduxKey].status)
   */
  reduxKey?: string;

  /**
   * The component to render for the step
   */
  component: ComponentType;

  /**
   * Check whether the user has completed the step based on the checkout state
   * This is used to send the user back to the first incomplete step
   *
   * TODO type the patient object
   */
  isComplete: (params: CheckoutStepParams) => boolean;

  /**
   * Whether the step is optional.
   * This is used for steps that should not be shown in certain cases.
   */
  isApplicable: (params: CheckoutStepParams) => boolean;
}

const isCanadianClinic = (clinic: any) => clinic?.address?.country === COUNTRY.canada;

/**
 * The ordered list of steps in the checkout process.
 * This should not be used directly to determine the current step.
 * Instead, use the `useCurrentCheckoutStep` hook to get the current step.
 */
export const stepDefinitions: CheckoutStepDefinition[] = [
  {
    id: StepId.INFO,
    name: 'Info',
    path: 'account',
    showInProgress: true,
    component: Info,
    isApplicable: () => true,
    isComplete: ({ isUserAuthenticated }: CheckoutStepParams) => {
      return isUserAuthenticated;
    },
  },
  { // Used if the user doesn't have a phone number associated with their account
    id: StepId.PHONE_NUMBER,
    name: 'Phone Number',
    path: 'phone-number',
    showInProgress: false,
    component: PhoneNumber,
    isApplicable: ({ patient }: CheckoutStepParams) => !patient?.mobilePhone,
    isComplete: ({ patient }: CheckoutStepParams) => Boolean(patient?.mobilePhone),
  },
  {
    id: StepId.ONGOING_CARE,
    name: 'Ongoing Care',
    path: 'ongoing-care',
    showInProgress: false,
    component: OngoingCareQuestion,
    isApplicable: ({ clinic, match }: CheckoutStepParams) => {
      if (!isCanadianClinic(clinic)) {
        return false;
      }

      return !match?.other?.ongoingCare;
    },
    isComplete: ({ match, checkoutOngoingCareAnswer }: CheckoutStepParams) => {
      return Boolean(match?.other?.ongoingCare || checkoutOngoingCareAnswer);
    },
  },
  {
    id: StepId.TREATMENTS,
    name: 'Treatments',
    path: 'treatments',
    showInProgress: false,
    component: TreatmentsQuestion,
    isApplicable: ({ clinic, match }: CheckoutStepParams) => {
      if (!isCanadianClinic(clinic)) {
        return false;
      }

      return !match?.other?.selectedTreatments?.length;
    },
    isComplete: ({ match, checkoutSelectedTreatments }: CheckoutStepParams) => {
      return Boolean(match?.other?.selectedTreatments?.length || checkoutSelectedTreatments?.length);
    },
  },
  {
    id: StepId.PATIENT_INFO,
    name: 'Insurance',
    path: 'patient',
    reduxKey: 'patient',
    showInProgress: true,
    component: PatientInfo,
    isApplicable: () => true,
    isComplete: ({ checkoutState }: CheckoutStepParams) => {
      const { firstName, lastName, dateOfBirth, sexAssignedAtBirth } = checkoutState.patient;
      const { day, month, year } = dateOfBirth;
      const flattenedFields = { firstName, lastName, sexAssignedAtBirth, day, month, year };

      const patientFormValidator = new FormValidator(PATIENT_INFO_VALIDATOR_CONFIG);
      const validationResult = patientFormValidator.validate(flattenedFields, { showReturningPatientToggle: false });
      return validationResult.allValid;
    },
  },
  {
    id: StepId.INSURANCE,
    name: 'Insurance',
    path: 'insurance',
    reduxKey: 'insurance',
    showInProgress: false,
    // Insurance contains nested routes
    component: InsuranceInfo,
    isApplicable: ({ match }: CheckoutStepParams) => match?.other?.insurance?.hasInsurance !== 'No',
    isComplete: ({ checkoutState, clinic }: CheckoutStepParams) => {
      const isUsClinic = !isCanadianClinic(clinic);
      const insuranceFormValidator = new FormValidator(INSURANCE_FORM_VALIDATOR_CONFIG);

      const { provider, plan, memberId, isPrimaryMember, primaryMember } = checkoutState.insurance;

      const insuranceFields = {
        insuranceProvider: provider,
        insurancePlan: plan,
        memberId,
        isPrimaryMember,
        primaryMemberFirstName: primaryMember?.firstName,
        primaryMemberLastName: primaryMember?.lastName,
        primaryMemberBirthYear: primaryMember?.dateOfBirth?.year,
        primaryMemberBirthMonth: primaryMember?.dateOfBirth?.month,
        primaryMemberBirthDay: primaryMember?.dateOfBirth?.day,
      };

      const params = { isUsClinic, isPrimaryMember: Boolean(isPrimaryMember) };
      const insuranceValidationResult = insuranceFormValidator.validate(insuranceFields, params);
      return insuranceValidationResult.allValid;
    },
  },
  {
    id: StepId.VERIFY,
    name: 'Verify',
    path: 'verify',
    reduxKey: 'verify',
    showInProgress: false,
    component: Verify,
    isApplicable: () => true,
    isComplete: ({ patient }: CheckoutStepParams) =>
      Boolean(patient.emailVerifiedAt && patient.phoneVerifiedAt),
  },
  {
    id: StepId.CONFIRM,
    name: 'Confirm',
    path: 'confirm',
    reduxKey: 'confirm',
    showInProgress: true,
    component: Confirm,
    isApplicable: () => true,
    isComplete: () => false,
  },
];